import { Component, ReactNode, useEffect, useState } from "react";
import { H } from "highlight.run";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { queryClient } from "./main";
import { Button } from "@/components/ui/button";
import Home from "./pages/Home";
import BudgetDetail from "./pages/BudgetDetail";
import Expenses from "./pages/Expenses";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Settings from "./pages/Settings";
import Badges from "./pages/Badges";
import Profile from "./pages/Profile";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import PrivacyAnalytics from "./pages/PrivacyAnalytics";
import Terms from "./pages/Terms";
import Imprint from "./pages/Imprint";
import DataExport from "./pages/DataExport";
import LanguageDebug from "./pages/LanguageDebug";
import TestRunner from "./pages/TestRunner";
import NotFound from "./pages/NotFound";
import Install from "./pages/Install";
import { ConsentBanner } from "./components/ConsentBanner";
import { UpdateNotification } from "./components/PWA/UpdateNotification";
import { DomainChangeBanner } from "./components/DomainChangeBanner";
import { consentManager } from "./lib/consent";
import { initAnalytics, unloadAnalytics } from "./lib/analytics";
import { initErrorTracking, updateErrorTrackingConsent } from "./lib/errorTracking";
import { initMaze, isTestMode } from "./lib/maze";
import { MAZE_CONFIG } from "./lib/config";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BudgetProvider } from "./contexts/BudgetContext";
import { GamificationProvider } from "./contexts/GamificationContext";
import { PWAProvider } from "./contexts/PWAContext";

// Error boundary that integrates with Highlight
class HighlightErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // Send error to Highlight
    H.consumeError(error, "React Error Boundary", errorInfo);
  }

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
          <div className="max-w-md w-full space-y-6 text-center">
            {/* Friendly icon */}
            <div className="text-5xl">🌿</div>
            
            {/* Heading */}
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Oops!</h1>
              <p className="text-muted-foreground">
                We hit a snag. Don't worry, your data is safe.
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => window.location.reload()} 
                className="w-full"
                size="lg"
              >
                Refresh Page
              </Button>
              <Button 
                onClick={this.handleGoHome}
                variant="outline"
                className="w-full"
                size="lg"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Inner App component that uses auth context
const AppContent = () => {
  const { user, isLoading } = useAuth();
  const [showBanner, setShowBanner] = useState(false);

  // Set banner visibility after auth check completes
  useEffect(() => {
    if (isLoading) return;
    
    const consent = consentManager.getConsent();
    const shouldShowBanner = !consent.bannerDismissed;
    setShowBanner(shouldShowBanner);
    
    console.log('[App] Initial consent check', {
      consent,
      shouldShowBanner,
      isAuthenticated: !!user,
    });
  }, [isLoading, user]);

  // Initialize analytics and error tracking based on consent (runs when auth state is ready)
  useEffect(() => {
    if (isLoading) return;

    const consent = consentManager.getConsent();
    
    console.log('[App] Initializing tracking (post-auth)', {
      consent,
      isLoading,
    });
    
    // Initialize error tracking based on consent
    initErrorTracking();
    
    // Load analytics if consent is granted
    if (consent.analytics) {
      console.log('[App] Auto-loading analytics (consent granted)');
      initAnalytics();
    } else {
      console.log('[App] Analytics not loaded (no consent)');
    }

    // Initialize Maze if in test mode
    if (MAZE_CONFIG.testModeOnly && isTestMode()) {
      console.log('[App] Test mode detected, initializing Maze');
      initMaze();
    } else if (!MAZE_CONFIG.testModeOnly && MAZE_CONFIG.enabled) {
      console.log('[App] Initializing Maze for all users');
      initMaze();
    }

    // Listen for consent changes
    const handleConsentChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      const newConsent = customEvent.detail;
      
      console.log('[App] Consent changed event received', newConsent);
      
      if (newConsent.analytics) {
        console.log('[App] Analytics enabled via consent change');
        initAnalytics();
      } else {
        console.log('[App] Analytics disabled via consent change');
        unloadAnalytics();
      }
      
      // Update error tracking based on crash consent
      updateErrorTrackingConsent(newConsent.crash);
      
      // Update banner visibility based on bannerDismissed state
      const shouldShow = !newConsent.bannerDismissed;
      setShowBanner(shouldShow);
      console.log('[App] Banner visibility updated', { shouldShow });
    };

    window.addEventListener('consent:changed', handleConsentChange);
    return () => window.removeEventListener('consent:changed', handleConsentChange);
  }, [isLoading]);

  const RouterShell = () => {
    const location = useLocation();

    // NOTE: current app logic uses this key across Auth/Home/Index.
    // We keep it to avoid behavior changes.
    const onboardingComplete = localStorage.getItem('onboardingComplete') === 'true';
    const isAuthRoute = location.pathname.startsWith('/auth');
    const isOnboardingRoute = location.pathname.startsWith('/onboarding');

    // Only start version polling (UpdateNotification mounts useVersionCheck) once:
    // 1) user is authenticated
    // 2) onboarding is complete
    // 3) user isn't currently on auth/onboarding routes
    const enableUpdateNotification = !!user && onboardingComplete && !isAuthRoute && !isOnboardingRoute;

    return (
      <>
        {enableUpdateNotification && <UpdateNotification />}
        <DomainChangeBanner />
        {!isLoading && showBanner && <ConsentBanner />}
        <BudgetProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/budget" element={<BudgetDetail />} />
            <Route path="/expenses" element={<Expenses />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/privacy-analytics" element={<PrivacyAnalytics />} />
            <Route path="/badges" element={<Badges />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/imprint" element={<Imprint />} />
            <Route path="/data-export" element={<DataExport />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/debug/language" element={<LanguageDebug />} />
            <Route path="/debug/tests" element={<TestRunner />} />
            <Route path="/install" element={<Install />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BudgetProvider>
      </>
    );
  };

  return (
    <>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <RouterShell />
      </BrowserRouter>
    </>
  );
};

// Main App wrapper with providers
const App = () => {
  return (
    <HighlightErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <PWAProvider>
            <GamificationProvider>
              <TooltipProvider>
                <AppContent />
              </TooltipProvider>
            </GamificationProvider>
          </PWAProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HighlightErrorBoundary>
  );
};

export default App;
